import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import './LandingPage.css';
import './Legal.css';

const LAST_UPDATED = 'June 26, 2026';

/**
 * Master Service Agreement (MSA) — governs the paid services offered through the
 * Platform (SMM orders, store purchases, escrow credits, and games). Written in
 * English as the authoritative version with a Spanish summary note.
 */
const MSAPage = () => {
  const { language } = useLanguage();
  const isES = language === 'es';

  useEffect(() => {
    const prev = document.title;
    document.title = 'Master Service Agreement — PHONESTREAM';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="landing-page neo">
      <Header />

      <main className="landing-main legal-main">
        <article className="legal-doc">
          <span className="neo-eyebrow accent-magenta">Legal</span>
          <h1>Master Service Agreement</h1>
          <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

          {isES && (
            <div className="legal-note">
              Este Acuerdo Marco de Servicios se publica en inglés como versión oficial.
              Regula los servicios de pago de la plataforma (pedidos SMM, compras en la
              tienda, créditos en escrow y juegos). Al usar estos servicios, aceptas
              este Acuerdo.
            </div>
          )}

          <p className="legal-lead">
            This Master Service Agreement (“MSA” or “Agreement”) governs paid services
            you order through PHONESTREAM (the “Platform”), including social media
            marketing (“SMM”) orders, digital store purchases, USDT escrow credits, and
            games. By depositing funds or placing an order, you (“Client”, “you”) agree
            to this MSA, which incorporates our{' '}
            <a href="/terms">Terms &amp; Conditions</a> and{' '}
            <a href="/privacy">Privacy Policy</a> by reference.
          </p>

          <h2>1. Definitions</h2>
          <ul>
            <li><strong>Services</strong> — the SMM orders, store products, escrow credit handling, and games offered on the Platform.</li>
            <li><strong>Escrow</strong> — the on-chain USDT escrow contract that holds your deposited credits until an order is captured, released, or refunded.</li>
            <li><strong>Order</strong> — a specific request for a Service (e.g. an SMM service for a given link and quantity, or a store product).</li>
            <li><strong>Provider</strong> — a third-party supplier (e.g. an SMM reseller API) used to fulfil an Order.</li>
          </ul>

          <h2>2. Accounts &amp; authorization</h2>
          <p>
            Services are accessed using your blockchain wallet. You are solely
            responsible for all activity conducted through your wallet and for keeping
            your wallet and keys secure. We authenticate you by wallet signature and act
            on instructions signed or submitted from your authenticated session.
          </p>

          <h2>3. Credits, deposits &amp; escrow</h2>
          <ul>
            <li>To use paid Services you deposit USDT, which is held as <strong>credits in escrow</strong>.</li>
            <li>When you place an Order, the corresponding amount is <strong>reserved</strong> from your available credits. On fulfilment it is <strong>captured</strong>; if cancelled or refunded, it is <strong>released</strong> back to you per Section 6.</li>
            <li>Credits are a prepaid balance for Services, are denominated in USDT, and are <strong>not</strong> a deposit account, e-money, security, or investment, and earn no interest.</li>
            <li>Withdrawal availability of unused credits depends on the Platform’s current configuration and operational status; during testing or maintenance phases withdrawals may be limited or unavailable, as disclosed in-product.</li>
          </ul>

          <h2>4. SMM services</h2>
          <ul>
            <li>SMM Orders are fulfilled through third-party Providers. Delivery speed, start time, drop/refill behavior, and availability are set by those Providers and are estimates, not guarantees.</li>
            <li>You are responsible for the legality of the links/URLs and accounts you submit and for complying with the terms of the destination platforms (e.g. Instagram, YouTube, TikTok, Spotify). Submitting links to content you are not authorized to promote is prohibited.</li>
            <li>We may reject, hold, or cancel any Order that violates this MSA, a Provider’s rules, or applicable law.</li>
            <li>An Order may be cancellable only while it is in a “Pending” state; once processing has begun, cancellation may not be possible.</li>
          </ul>

          <h2>5. Store products</h2>
          <p>
            Digital products are licensed, not sold, for your permitted use as described
            on each product page. Because products are digital and delivered
            immediately, all sales are final except where required by law or where a
            product is materially not as described.
          </p>

          <h2>6. Refunds</h2>
          <ul>
            <li>If an SMM Order is not delivered, is partially delivered, or is cancelled, a corresponding refund of the undelivered portion is credited back to your escrow balance, net of any non-recoverable Provider costs.</li>
            <li>Refunds are calculated from Provider-reported delivery (delivered vs. remaining quantity) and are recorded in a refund ledger with the associated transaction reference.</li>
            <li>Refund timing depends on Provider responses and on-chain confirmation and is not instantaneous.</li>
            <li>Game outcomes (wins/losses) are final and are not refundable, except in the case of a verified technical error as solely determined by us.</li>
          </ul>

          <h2>7. Fees, pricing &amp; discounts</h2>
          <p>
            Prices, rates (e.g. per-1,000 SMM rates), and any platform fees are shown at
            the time of Order and may change. Discounts (for example holder discounts)
            apply only while active and under the conditions shown. The total you pay is
            displayed in the Order summary before you confirm.
          </p>

          <h2>8. Games</h2>
          <p>
            Games are offered for entertainment using your USDT balance and carry the
            risk of loss. Payouts, multipliers, house edge, and limits are configured by
            the Platform and shown in-product. You must be of legal age and located where
            participation is lawful. Do not play with funds you cannot afford to lose.
          </p>

          <h2>9. Acceptable use</h2>
          <ul>
            <li>No use of the Services for unlawful, fraudulent, deceptive, or abusive purposes, including money laundering, sanctions evasion, or market manipulation.</li>
            <li>No attempts to exploit, reverse-engineer, overload, or interfere with the Platform, the escrow contract, or the games.</li>
            <li>No use of bots, multiple wallets, or other means to abuse promotions, discounts, or game logic.</li>
          </ul>

          <h2>10. Third-party providers</h2>
          <p>
            Some Services rely on third parties (SMM resellers, RPC nodes, Spotify, the
            AI provider, and the cloud database). We are not responsible for the acts,
            omissions, downtime, or policy changes of those third parties, and their
            performance is outside our control.
          </p>

          <h2>11. Disclaimers &amp; limitation of liability</h2>
          <p>
            The Services are provided “as is” and “as available”, without warranties of
            any kind. To the maximum extent permitted by law, PHONESTREAM is not liable
            for indirect, incidental, special, consequential, or punitive damages, or for
            lost profits, revenue, data, or credits, arising from the Services, Provider
            performance, blockchain or RPC failures, or smart-contract risk. Our total
            aggregate liability for a given Order is limited to the amount you paid for
            that Order.
          </p>

          <h2>12. Suspension &amp; termination</h2>
          <p>
            We may suspend or terminate access to the Services, and hold or reverse
            Orders, where we reasonably suspect a breach of this MSA, fraud, abuse, or a
            legal/security risk. You may stop using the Services at any time.
          </p>

          <h2>13. Changes &amp; contact</h2>
          <p>
            We may update this MSA to reflect legal, technical, or business changes; the
            “Last updated” date above will change accordingly. Continued use of the
            Services after an update constitutes acceptance. For questions about this MSA,
            contact the PHONESTREAM team through the official channels listed on the
            website or our community servers.
          </p>

          <div className="legal-note">
            This Agreement should be read together with our{' '}
            <a href="/terms">Terms &amp; Conditions</a> and{' '}
            <a href="/privacy">Privacy Policy</a>.
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default MSAPage;
